import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api";
import { Stack, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const ROLE_HOME = { super_admin: "/", manager: "/", prod_entry: "/production", job_entry: "/jobs", dispatch: "/dispatch-slips" };

  if (user) return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      nav(ROLE_HOME[u.role] || "/");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[var(--text-1)]">
        <img
          src="https://images.unsplash.com/photo-1610891015188-5369212db097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMGZhY3RvcnklMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc4MjQ0MTI4MHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white text-black flex items-center justify-center rounded-sm">
              <Stack size={22} weight="bold" />
            </div>
            <div className="heading font-black text-xl">KAPSTONE OPS</div>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">Plant intelligence</div>
            <h1 className="heading text-4xl font-black tracking-tight leading-tight max-w-md">
              Monitor every board. Every cut. Every job.
            </h1>
            <p className="mt-4 text-white/70 max-w-md">
              A clean console for board, printing, gluing and stitching — pending counts deducted in real-time as your floor moves.
            </p>
          </div>
          <div className="text-xs text-white/50 mono">v1.0 · {new Date().getFullYear()}</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-[var(--surface)]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="overline mb-3">Sign in</div>
          <h2 className="heading text-3xl font-black mb-8">Welcome back.</h2>

          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-2)]">Email</label>
          <input
            data-testid="login-email"
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-1 mb-4 px-3 py-2.5 border border-[var(--border)] rounded-sm bg-white focus:ring-2 focus:ring-[var(--accent)] focus:outline-none"
            required
          />
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-2)]">Password</label>
          <input
            data-testid="login-password"
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-1 mb-6 px-3 py-2.5 border border-[var(--border)] rounded-sm bg-white focus:ring-2 focus:ring-[var(--accent)] focus:outline-none"
            required
          />

          {err && <div className="mb-4 text-sm text-[var(--critical)]" data-testid="login-error">{err}</div>}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-[var(--text-1)] text-white py-3 rounded-sm font-semibold flex items-center justify-center gap-2 hover:bg-zinc-800 transition-colors disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"} <ArrowRight size={16} weight="bold" />
          </button>

          <div className="mt-6 text-xs text-[var(--text-3)]" data-testid="login-help">
            Contact your administrator if you need access.
          </div>
        </form>
      </div>
    </div>
  );
}
